import { useMutation, useQueryClient } from '@tanstack/react-query';

import { hasKey } from '~/utils';
import { config } from '~/config';
import { useRequest, HttpError } from '~/lib/useRequest';
import { Application, PatchApplicationResponse } from '@ab/ams-school-api/types/private/v1';

export type PatchBodyProgramSelected = {
  programSelected: {
    priority: number;
    programIntakeId: string;
  };
};

export function usePatchProgram(id: string) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const { isLoading, error, mutateAsync } = useMutation(
    ['application', id],
    async (payload: PatchBodyProgramSelected) => {
      try {
        const response = await request<PatchApplicationResponse>(
          `${config.apiHost}/private/v1/applications/${id}/program_selected`,
          {
            method: 'PATCH',
            body: {
              data: {
                id: id,
                type: 'application',
                attributes: payload,
              },
            },
            isExpectedResponse,
          }
        );

        const app = response.data;
        const attrs = app.attributes;

        queryClient.invalidateQueries(['application', id]);

        return {
          ...attrs,
          id: app.id,
        } as Application;
      } catch (err) {
        if (isInvalidApplicationStatusError(err)) throw new HttpError(400, 'invalid status', err.response, err.body);
        throw err;
      }
    }
  );

  return {
    patchProgram: mutateAsync,
    patchProgramError: error,
    isPatchingProgram: isLoading,
  };
}
function isExpectedResponse(res: unknown): res is PatchApplicationResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
function isInvalidApplicationStatusError(err: unknown | Error): err is HttpError {
  if (err instanceof HttpError && err.body?.errors?.[0]?.detail === 'Invalid application status') return true;
  return false;
}
